import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Pencil, Star, Trash2, Users } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatNumber } from '../../../components/design-system/admin/format';
import type { VendorPlan } from '../our-vendors/models/vendor.model';
import { PLAN_ADDONS, PLAN_GROUPS, addonEnabled, formatPlanPrice, moduleEnabled, priceKind } from './plan-catalog';
import type { PlanModule } from './plan-catalog';
import { usePlanLabels } from './use-plan-labels';
import type { PlanLabels } from './use-plan-labels';
import { PlanIcon } from './PlanIcon';
import { IncludedMark } from './IncludedMark';

/** "100 credits / month · High quality · Seamless textures" — what an enabled module comes with. */
function moduleSummary(plan: VendorPlan, m: PlanModule, labels: PlanLabels): string {
  const parts: string[] = [];
  m.limits.forEach((k) => {
    const v = plan.limits?.[k];
    if (v === undefined || v === null) return;
    parts.push(`${labels.limitValue(v)} ${labels.limitUnit(k)}`);
  });
  if (m.quality && plan.features?.visualizationQuality) parts.push(labels.qualityFull(plan.features.visualizationQuality));
  m.extras.forEach((k) => {
    if (plan.features?.[k]) parts.push(labels.featureLabel(k));
  });
  return parts.join(' · ');
}

export interface PlanCardProps {
  plan: VendorPlan;
  /** Vendors on the plan; `undefined` while it loads, `null` when unknown (then not shown). */
  vendors: number | null | undefined;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * One plan in the plans list: price, who is on it, and what it includes —
 * module by module, then add-ons. Dragged by its handle only, so its buttons
 * stay clickable. The default plan wears an ink outline.
 */
export function PlanCard({ plan, vendors, onEdit, onDelete }: PlanCardProps) {
  const { t } = useTranslation();
  const labels = usePlanLabels();
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({ id: plan._id });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
    opacity: isDragging ? 0.9 : 1,
  };
  const kind = priceKind(plan);
  const isDefault = Boolean(plan.isDefault);

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`admin-plan-card-${plan.slug}`}
      data-default={isDefault ? 'true' : 'false'}
      className="relative"
    >
      <Card className={cn('h-full gap-0 py-0', isDefault && 'ring-[1.5px] ring-foreground', isDragging && 'shadow-lg')}>
        <div className="flex flex-col gap-3 px-5 pt-4 pb-5">
          <div className="flex items-center gap-1.5">
            <span
              {...attributes}
              {...listeners}
              data-testid={`admin-plan-drag-${plan.slug}`}
              aria-label={t('PLANS.ADMIN.DRAG_HANDLE')}
              className={cn(
                '-ms-1.5 flex size-7 shrink-0 touch-none items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                isDragging ? 'cursor-grabbing' : 'cursor-grab',
              )}
            >
              <GripVertical className="size-4" />
            </span>
            <Badge variant="outline" className="min-w-0 shrink font-mono text-[10px] font-normal text-muted-foreground">
              <span className="min-w-0 truncate">{plan.slug}</span>
            </Badge>
            {isDefault && (
              <Badge variant="outline" className="border-foreground/30">
                <Star className="fill-current" />
                {t('PLANS.ADMIN.DEFAULT')}
              </Badge>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('ADMIN_SHELL.COMMON.DELETE')}
                  className="ms-auto shrink-0 text-muted-foreground hover:bg-destructive/5 hover:text-destructive"
                  onClick={onDelete}
                  data-testid={`admin-plan-delete-${plan.slug}`}
                >
                  <Trash2 />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('ADMIN_SHELL.COMMON.DELETE')}</TooltipContent>
            </Tooltip>
          </div>

          <div className="min-w-0">
            <h2 className="truncate font-heading text-lg font-medium tracking-tight">{plan.name}</h2>
            {kind !== 'none' && (
              <p className="mt-1 flex items-baseline gap-1">
                <span className="font-heading text-2xl leading-tight font-medium tracking-tight tabular-nums">
                  {kind === 'custom' ? t('PLANS.PRICE_CUSTOM') : kind === 'free' ? t('PLANS.PRICE_FREE') : formatPlanPrice(plan)}
                </span>
                {kind === 'paid' && <span className="text-sm text-muted-foreground">/{t('PLANS.PER_MONTH')}</span>}
              </p>
            )}
          </div>

          {plan.description && <p className="text-sm leading-relaxed text-muted-foreground">{plan.description}</p>}

          {vendors === undefined ? (
            <Skeleton className="h-4 w-24" />
          ) : (
            vendors !== null && (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`admin-plan-vendors-${plan.slug}`}>
                <Users className="size-3.5" aria-hidden />
                <span className="font-medium text-foreground tabular-nums">{formatNumber(vendors)}</span>
                <span>{t('ADMIN_PLANS.LIST.VENDORS', { count: vendors })}</span>
              </p>
            )
          )}
        </div>

        <div className="flex flex-1 flex-col gap-3 border-t px-5 py-4">
          {PLAN_GROUPS.map((m) => {
            const enabled = moduleEnabled(plan.features, m);
            const summary = enabled ? moduleSummary(plan, m, labels) : '';
            return (
              <div key={m.id}>
                <div className="flex items-center gap-2.5">
                  <PlanIcon Icon={m.Icon} size="sm" className={cn(!enabled && 'opacity-50')} />
                  <span className={cn('flex-1 text-sm', enabled ? 'font-medium' : 'text-muted-foreground')}>{labels.moduleTitle(m)}</span>
                  <IncludedMark included={enabled} />
                </div>
                {summary && <p className="mt-0.5 ps-[34px] text-xs leading-normal text-muted-foreground">{summary}</p>}
              </div>
            );
          })}

          <p className="mt-2 text-xs font-medium text-muted-foreground">{t('ADMIN_PLANS.ADDONS')}</p>
          {PLAN_ADDONS.map((a) => {
            const enabled = addonEnabled(plan.features, a);
            return (
              <div key={a.id} className="flex items-center gap-2.5">
                <PlanIcon Icon={a.Icon} size="sm" className={cn(!enabled && 'opacity-50')} />
                <span className={cn('flex-1 text-sm', !enabled && 'text-muted-foreground')}>{labels.addonTitle(a)}</span>
                <IncludedMark included={enabled} />
              </div>
            );
          })}
        </div>

        <div className="border-t px-5 py-3">
          <Button variant="outline" className="w-full" data-testid={`admin-plan-edit-${plan.slug}`} onClick={onEdit}>
            <Pencil />
            {t('PLANS.ADMIN.EDIT')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
